import React, { useEffect, useMemo, useState } from 'react';
import {
  ADAPTIVE_DISPLAY_EXPLANATION,
  BACKUP_BROWSER_DISPLAY_NAME,
  DEFAULT_SEARCH_ENGINE,
  DEFAULT_STARTUP_URL,
  DISPLAY_MODE_ADAPTIVE,
  generateBrowserProfileCommands,
  getCamoufoxOsLabel,
  PRIMARY_BROWSER_DISPLAY_NAME,
  PRIMARY_BROWSER_PROFILE_SLUG,
  sanitizeBrowserProfileName,
  STARTUP_URL_PRESETS,
} from '../../../services/funding/fundingBrowserProfilesService';

export default function CreateBrowserProfileModal({
  open,
  clientId,
  hasPrimaryProfile = false,
  deviceSettings,
  os,
  onClose,
  onSubmit,
  submitting = false,
}) {
  const [form, setForm] = useState({
    profileName: PRIMARY_BROWSER_PROFILE_SLUG,
    startupPreset: 'google',
    startupUrl: DEFAULT_STARTUP_URL,
    searchEngine: DEFAULT_SEARCH_ENGINE,
    displayMode: DISPLAY_MODE_ADAPTIVE,
  });
  const [error, setError] = useState('');
  const effectiveOs = os || deviceSettings?.os;

  useEffect(() => {
    if (!open) return;
    setForm({
      profileName: hasPrimaryProfile ? '' : PRIMARY_BROWSER_PROFILE_SLUG,
      startupPreset: 'google',
      startupUrl: DEFAULT_STARTUP_URL,
      searchEngine: DEFAULT_SEARCH_ENGINE,
      displayMode: DISPLAY_MODE_ADAPTIVE,
    });
    setError('');
  }, [open, hasPrimaryProfile]);

  const preview = useMemo(() => {
    if (!clientId || !form.profileName) return null;
    try {
      const profileName = sanitizeBrowserProfileName(form.profileName);
      return generateBrowserProfileCommands({
        clientId,
        profileName,
        startupUrl: form.startupUrl,
        os: effectiveOs,
        fallbackWidth: deviceSettings?.fallbackWidth,
        fallbackHeight: deviceSettings?.fallbackHeight,
        fallbackOs: effectiveOs,
      });
    } catch (err) {
      return { error: err.message };
    }
  }, [
    clientId,
    deviceSettings?.fallbackHeight,
    deviceSettings?.fallbackWidth,
    effectiveOs,
    form.profileName,
    form.startupUrl,
  ]);

  const modalTitle = hasPrimaryProfile
    ? 'Add Backup / Testing Browser'
    : 'Create Primary Browser';

  const submitLabel = hasPrimaryProfile
    ? 'Add Backup Browser'
    : 'Create Primary Browser';

  if (!open) return null;

  const handleStartupPresetChange = (presetId) => {
    const preset = STARTUP_URL_PRESETS.find((item) => item.id === presetId);
    setForm((current) => ({
      ...current,
      startupPreset: presetId,
      startupUrl: presetId === 'custom'
        ? (current.startupPreset === 'custom' ? current.startupUrl : '')
        : (preset?.url || DEFAULT_STARTUP_URL),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError('');

    if (!clientId) {
      setError('Browser profiles require a saved funding client ID.');
      return;
    }

    if (!form.profileName.trim()) {
      setError('Profile folder slug is required for backup or testing browsers.');
      return;
    }

    if (form.startupPreset === 'custom' && !form.startupUrl.trim()) {
      setError('Enter a custom startup URL or choose Google or BrowserLeaks.');
      return;
    }

    if (preview?.error) {
      setError(preview.error);
      return;
    }

    const profileName = sanitizeBrowserProfileName(form.profileName);
    const displayName = profileName === PRIMARY_BROWSER_PROFILE_SLUG
      ? PRIMARY_BROWSER_DISPLAY_NAME
      : (profileName === 'backup' ? BACKUP_BROWSER_DISPLAY_NAME : undefined);

    onSubmit?.({
      ...form,
      profileName,
      displayName,
      displayMode: DISPLAY_MODE_ADAPTIVE,
      startupUrl: form.startupUrl.trim() || DEFAULT_STARTUP_URL,
      searchEngine: DEFAULT_SEARCH_ENGINE,
      os: effectiveOs,
      fallbackWidth: deviceSettings?.fallbackWidth,
      fallbackHeight: deviceSettings?.fallbackHeight,
      detectedOs: effectiveOs,
      detectedScreenWidth: deviceSettings?.fallbackWidth,
      detectedScreenHeight: deviceSettings?.fallbackHeight,
      devicePixelRatio: deviceSettings?.devicePixelRatio,
    });
  };

  return (
    <div className="fg-modal-backdrop is-open" data-testid="create-browser-profile-modal">
      <div className="fg-modal fg-browser-profile-modal" role="dialog" aria-modal="true">
        <button type="button" className="fg-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="fg-modal-header">
          <div className="fg-modal-icon">CF</div>
          <div>
            <h2 data-testid="browser-profile-modal-title">{modalTitle}</h2>
            <p>
              {hasPrimaryProfile
                ? 'Add an optional backup or testing Camoufox profile. Extra profiles are manual and intentional.'
                : 'Generate the primary Camoufox profile for this client. Commands are copyable and safe to run locally.'}
            </p>
          </div>
        </div>

        <form className="fg-profile-form" noValidate onSubmit={handleSubmit}>
          <div className="fg-form-grid">
            <label className="fg-form-span-2">
              {hasPrimaryProfile ? 'Backup profile folder slug' : 'Primary profile folder slug'}
              <input
                type="text"
                className="fg-application-input"
                value={form.profileName}
                readOnly={!hasPrimaryProfile}
                placeholder={hasPrimaryProfile ? 'backup-testing' : PRIMARY_BROWSER_PROFILE_SLUG}
                data-testid="browser-profile-name-input"
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    profileName: event.target.value,
                  }))
                }
              />
              {!hasPrimaryProfile ? (
                <span className="fg-field-hint">
                  {`Display name: ${PRIMARY_BROWSER_DISPLAY_NAME}`}
                </span>
              ) : (
                <span className="fg-field-hint">
                  {`Optional display name for slug "backup": ${BACKUP_BROWSER_DISPLAY_NAME}`}
                </span>
              )}
            </label>

            <div className="fg-form-span-2 fg-browser-display-mode" data-testid="browser-profile-display-mode">
              <span className="fg-browser-search-helper__label">Display Mode</span>
              <div className="fg-browser-display-mode__badges">
                <span>Display: Adaptive</span>
                <span>Screen: Detected at launch</span>
                <span>Theme: Light native controls</span>
              </div>
              <p className="fg-field-hint" data-testid="browser-profile-display-mode-help">
                {ADAPTIVE_DISPLAY_EXPLANATION}
              </p>
              {deviceSettings ? (
                <p className="fg-field-hint" data-testid="browser-profile-detected-preview">
                  {`Detected now: ${getCamoufoxOsLabel(effectiveOs)} · ${deviceSettings.fallbackWidth} × ${deviceSettings.fallbackHeight}. The local launcher reads this computer again when Camoufox starts.`}
                </p>
              ) : null}
            </div>

            <div className="fg-form-span-2 fg-browser-startup-options">
              <span className="fg-browser-search-helper__label">Startup URL</span>
              <div className="fg-browser-startup-options__choices">
                {STARTUP_URL_PRESETS.map((preset) => (
                  <label key={preset.id} className="fg-browser-startup-option">
                    <input
                      type="radio"
                      name="startupPreset"
                      value={preset.id}
                      checked={form.startupPreset === preset.id}
                      data-testid={`browser-profile-startup-preset-${preset.id}`}
                      onChange={() => handleStartupPresetChange(preset.id)}
                    />
                    <span>{preset.label}</span>
                  </label>
                ))}
              </div>
              {form.startupPreset === 'custom' ? (
                <input
                  type="url"
                  className="fg-application-input"
                  value={form.startupUrl}
                  placeholder="https://example.com"
                  data-testid="browser-profile-custom-startup-url"
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      startupUrl: event.target.value,
                    }))
                  }
                />
              ) : (
                <code className="fg-browser-startup-options__preview" data-testid="browser-profile-startup-url-preview">
                  {form.startupUrl}
                </code>
              )}
            </div>
          </div>

          {preview?.userDataDir && (
            <div className="fg-generated-preview">
              <h3>Generated path</h3>
              <code data-testid="browser-profile-generated-path">{preview.userDataDir}</code>
              {preview.scriptFilename ? (
                <code data-testid="browser-profile-generated-script">{preview.scriptFilename}</code>
              ) : null}
            </div>
          )}

          {(error || preview?.error) && (
            <p className="fg-form-error" data-testid="browser-profile-form-error">
              {error || preview.error}
            </p>
          )}

          <div className="fg-modal-actions fg-modal-footer">
            <button type="button" className="fg-btn fg-btn-light" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="fg-btn fg-btn-primary"
              data-testid="browser-profile-generate-btn"
              disabled={submitting || !clientId}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
